import { HourglassIcon } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  WAITING_FOR_A_SLOT,
  ordinal,
  queuedReasonSentence,
  useWorkspaceConcurrencyLimit,
} from '@/features/concurrency';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import { NeedsAccountReason } from '@/features/identity';
import { useCanWork } from '@/features/workspaces/canWork';
import { statusPresentationFor } from '../statusPresentation';
import { BLOCKED_SCOPE_LABEL } from '../executionStatus';
import type { CardExecutionState } from '../executionStatus';
import { waitingOnChildrenLabel } from '../linkedWaiting';

export interface ExecutionIndicatorProps {
  state: CardExecutionState;
  /**
   * `compact` is the chip on a board card: only the three statuses that want
   * something from someone appear, and a failure opens its detail in place.
   * `full` is the task detail's block, where every status is named outright,
   * including the two a card leaves silent.
   */
  density?: 'compact' | 'full';
}

/**
 * What a task is doing, in words. A bare spinner in the corner of a card reads
 * as "this card is loading" rather than "an agent is working on this task",
 * and the states that carry no motion at all had only a `title` attribute,
 * which never reaches a keyboard or touch user. Each state pairs its glyph
 * with a label and one of the board's state colours.
 */
export function ExecutionIndicator({
  state,
  density = 'compact',
}: ExecutionIndicatorProps) {
  const workspaceLimit = useWorkspaceConcurrencyLimit();
  // A reader is told a gate waits on a member, not on them (spec 193).
  const presentation = statusPresentationFor(state.kind, useCanWork());
  if (!presentation) return null;

  const { Icon, colourClass, cardLabel, canonicalName, announces, spin } =
    presentation;
  const compact = density === 'compact';

  // A card shows only what wants something from someone.
  if (compact && cardLabel === null) return null;

  const label = compact ? cardLabel : canonicalName;
  const sizeClasses = compact ? 'size-3.5' : 'size-4';
  const textClasses = compact ? 'text-2xs' : 'text-sm';

  const glyph = (
    <Icon
      aria-hidden="true"
      className={`${sizeClasses} ${spin ? 'animate-spin' : ''}`}
      strokeWidth={1.5}
    />
  );

  /**
   * The words, plus — where the card's wording differs from the canonical name
   * — the canonical one for assistive technology. The visible half is taken
   * out of the accessibility tree so the two are never read one after the
   * other.
   */
  const words =
    label === canonicalName ? (
      label
    ) : (
      <>
        <span aria-hidden="true">{label}</span>
        <span className="sr-only">{canonicalName}</span>
      </>
    );

  // A parent waiting on the tasks its run created (spec 140 US5). Nothing runs
  // on it, so no spinner and no announcement: an hourglass, muted, and how
  // many are still open. The same words in both densities.
  if (state.kind === 'live' && state.children) {
    return (
      <div
        className={`text-muted-foreground inline-flex items-center gap-1.5 ${textClasses} font-medium`}
      >
        <HourglassIcon
          aria-hidden="true"
          className={sizeClasses}
          strokeWidth={1.5}
        />
        {waitingOnChildrenLabel(state.children)}
      </div>
    );
  }

  // Failures and blocks on a card open their detail in place: triaging one should not
  // cost you the board you were reading.
  //
  // Both, not just failures. A block carries a message the reader needs — which limit
  // stopped it and who to ask — and a chip reading "Spend limit" with no way to reach
  // that message would be the same dead end the status exists to prevent.
  if (
    (state.kind === 'failed' ||
      state.kind === 'blocked' ||
      state.kind === 'needs-account') &&
    compact
  ) {
    return (
      /*
        `ui/popover`, not a hand-positioned panel.

        This used to be an absolutely positioned `role="dialog"` div with its
        own document-level `keydown` and `pointerdown` listeners standing in for
        Escape and click-outside — and with no collision detection at all, so
        near the right edge of a board it rendered off-canvas. The primitive
        does all three, and this is now its first consumer: `ui/popover.tsx` was
        in the tree with nothing importing it.
      */
      <Popover>
        <PopoverTrigger
          render={
            <button
              type="button"
              onClick={(event) => {
                // The card around this is itself activatable; opening the
                // failure detail must not also open the task.
                event.stopPropagation();
              }}
              aria-label={
                state.kind === 'failed'
                  ? 'View failure details'
                  : 'View why this was stopped'
              }
              className={`${colourClass ?? ''} hover:bg-accent ${FOCUS_RING} inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium transition-colors motion-safe:duration-[var(--motion-duration-base)]`}
            />
          }
        >
          {glyph}
          {label}
        </PopoverTrigger>

        <PopoverContent align="end" className="min-w-64 p-3">
          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground text-xs font-medium">
              {state.kind === 'failed' ? 'Failed on: ' : 'Stopped on: '}
              <span className="text-foreground">{state.stepLabel}</span>
            </p>
            {/*
              Which budget refused it (spec 080 FR-019). Read from the value the service
              sends, never from the sentence below — the two tiers are released by
              different people, and a reader who cannot tell them apart cannot act.
            */}
            {state.kind === 'blocked' && state.scope !== null ? (
              <p className="text-muted-foreground text-xs font-medium">
                Stopped by:{' '}
                <span className="text-foreground">
                  {BLOCKED_SCOPE_LABEL[state.scope]}
                </span>
              </p>
            ) : null}
            {state.kind === 'needs-account' ? (
              <NeedsAccountReason
                hosts={state.hosts}
                personId={state.personId}
                personName={state.personName}
              />
            ) : (
              <p className="text-foreground text-xs">{state.message}</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // A queued run says where it stands and why (spec 115 FR-002), as a ticket stub
  // since spec 118 (FR-009): its place in a solid badge joined to "Waiting for a
  // slot", so a waiting card reads as waiting from across the board. The reason is
  // on hover and — since hover never reaches a keyboard or touch user — to
  // assistive technology as well. No glyph and no motion: it is not working yet.
  if (state.kind === 'queued') {
    const reason = queuedReasonSentence(state.reason, workspaceLimit);
    return (
      <span
        data-stub="queued"
        title={reason ?? undefined}
        className={`border-primary/55 text-primary inline-flex items-stretch self-start overflow-hidden rounded-md border ${textClasses} font-medium`}
      >
        {state.position === null ? null : (
          <span className="bg-primary text-primary-foreground px-1.5 font-mono font-bold">
            {ordinal(state.position)}
          </span>
        )}
        <span
          className={`px-2 ${state.position === null ? '' : 'border-primary/55 border-l border-dashed'}`}
        >
          {WAITING_FOR_A_SLOT}
        </span>
        {reason ? <span className="sr-only">{reason}</span> : null}
      </span>
    );
  }

  return (
    <div
      {...(announces ? { role: 'status', 'aria-live': 'polite' as const } : {})}
      className={`${colourClass ?? ''} inline-flex items-center gap-1.5 ${textClasses} font-medium`}
    >
      {glyph}
      {words}
    </div>
  );
}
